import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import type {
  TemplateVersion,
  TemplateVersionInfo,
} from '../../types/templateModel'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'

const templateDescendantIds = (forTemplate: TemplateVersionInfo): string[] => {
  const childDescendants = forTemplate.children.flatMap(templateDescendantIds)

  return [forTemplate.identifier, ...childDescendants]
}

/**
 * Hooks used for resolving the list of templates that can be set as replaces by a
 * given template. Validates the returned templates have the same model type as the
 * targeted template, and that they're not descendants of the template
 */
export const useTemplateReplacesOptions = (
  templateVersion: Optional<TemplateVersion>,
) => {
  const allDescendants = useMemo(
    () =>
      templateVersion ? templateDescendantIds(templateVersion.versionInfo) : [],
    [templateVersion],
  )
  const { applicationTemplates: applicationTemplateOperations } =
    useApplicationViewOperationsContext()

  return useMemo(() => {
    if (!templateVersion?.typeName) {
      return []
    }

    const operationsForReplacesCandidates = applicationTemplateOperations
      .values()
      .toArray()

    return operationsForReplacesCandidates
      .filter(
        (createOperations) =>
          !!createOperations.lastEntity &&
          createOperations.lastEntity.templateTypeName ===
            templateVersion.typeName &&
          !allDescendants.includes(createOperations.templateId),
      )
      .map((createOperations) => createOperations.templateId)
      .sort((templateId1, templateId2) =>
        templateId1.localeCompare(templateId2),
      )
  }, [allDescendants, applicationTemplateOperations, templateVersion?.typeName])
}
